import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'localization';
import { ItemForkGrant } from 'types/channel';
import ButtonAtom from '../../../atoms/Button/ButtonAtom';
import './ForkGrantSearchMolecule.scss';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export interface ForkGrantSearchMoleculeProps {
  grants: ItemForkGrant[];
  onAdd: (identityId: string) => Promise<void>;
  onRemove: (identityId: string) => Promise<void>;
  isMutating: boolean;
  className?: string;
}

const ForkGrantSearchMolecule: React.FC<ForkGrantSearchMoleculeProps> = ({
  grants,
  onAdd,
  onRemove,
  isMutating,
  className = '',
}) => {
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedInput(searchInput.trim());
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput]);

  const existingIds = useMemo(
    () => new Set(grants.map((g) => g.identityId)),
    [grants]
  );

  const isAlreadyGranted = debouncedInput.length > 0 && existingIds.has(debouncedInput);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setError(null);
  }, []);

  const handleAdd = useCallback(async () => {
    if (!debouncedInput || isAlreadyGranted || isMutating) return;
    setError(null);
    try {
      await onAdd(debouncedInput);
      setSearchInput('');
      setDebouncedInput('');
    } catch {
      setError(t('common.error.unknown'));
    }
  }, [debouncedInput, isAlreadyGranted, isMutating, onAdd, t]);

  const handleRemove = useCallback(
    async (grant: ItemForkGrant) => {
      const name = grant.displayName || grant.identityId;
      // eslint-disable-next-line no-alert
      if (!globalThis.confirm(t('marketplace.itemDetail.permissions.removeForkGrantConfirm', { name }))) {
        return;
      }
      setError(null);
      try {
        await onRemove(grant.identityId);
      } catch {
        setError(t('common.error.unknown'));
      }
    },
    [onRemove, t]
  );

  const handleLoadMore = useCallback(() => {
    setVisibleCount((c) => c + PAGE_SIZE);
  }, []);

  const visibleGrants = grants.slice(0, visibleCount);
  const hasMore = grants.length > visibleCount;

  const classes = ['marketplace-fork-grant-search-molecule', className].filter(Boolean).join(' ');

  return (
    <section className={classes} data-testid="fork-grant-search">
      <header className="marketplace-fork-grant-search-molecule__header">
        <h4 className="marketplace-fork-grant-search-molecule__title">
          {t('marketplace.itemDetail.permissions.forkGrants')}
        </h4>
        <p className="marketplace-fork-grant-search-molecule__hint">
          {t('marketplace.itemDetail.permissions.forkGrantsHint')}
        </p>
      </header>

      <div className="marketplace-fork-grant-search-molecule__search">
        <label
          htmlFor="fork-grant-search-input"
          className="marketplace-fork-grant-search-molecule__label"
        >
          {t('marketplace.itemDetail.permissions.addForkGrant')}
        </label>
        <div className="marketplace-fork-grant-search-molecule__search-row">
          <input
            id="fork-grant-search-input"
            type="text"
            className="marketplace-fork-grant-search-molecule__input"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={t('marketplace.itemDetail.permissions.searchUsers')}
            disabled={isMutating}
            autoComplete="off"
          />
          <ButtonAtom
            type="button"
            variant="primary"
            shape="pill"
            size="small"
            label={t('marketplace.itemDetail.permissions.addForkGrant')}
            onClick={handleAdd}
            disabled={!debouncedInput || isAlreadyGranted || isMutating}
            data-testid="fork-grant-add-button"
          />
        </div>
      </div>

      {error && (
        <div className="marketplace-fork-grant-search-molecule__error" role="alert">
          {error}
        </div>
      )}

      {grants.length === 0 ? (
        <p
          className="marketplace-fork-grant-search-molecule__empty"
          data-testid="fork-grant-empty"
        >
          {t('marketplace.itemDetail.permissions.noForkGrants')}
        </p>
      ) : (
        <ul className="marketplace-fork-grant-search-molecule__list">
          {visibleGrants.map((grant) => {
            const name = grant.displayName || grant.identityId;
            return (
              <li
                key={grant.identityId}
                className="marketplace-fork-grant-search-molecule__item"
              >
                <span className="marketplace-fork-grant-search-molecule__item-name">
                  {name}
                </span>
                <ButtonAtom
                  type="button"
                  variant="text"
                  shape="pill"
                  size="small"
                  icon="pi pi-times"
                  label={t('marketplace.itemDetail.permissions.removeForkGrant')}
                  onClick={() => handleRemove(grant)}
                  disabled={isMutating}
                  data-testid={`fork-grant-remove-${grant.identityId}`}
                />
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <div className="marketplace-fork-grant-search-molecule__load-more">
          <ButtonAtom
            type="button"
            variant="text"
            shape="pill"
            size="small"
            label={t('common.loadMore')}
            onClick={handleLoadMore}
            data-testid="fork-grant-load-more"
          />
        </div>
      )}
    </section>
  );
};

export default ForkGrantSearchMolecule;
